import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { Box, IconButton, Tooltip, Typography, Paper } from '@mui/material';
import { Button, Container, Grid } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import * as Sentry from '@sentry/react';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';

import CustomBackdrop from '../../../../components/CustomBackdrop/CustomBackdrop';
import { PD_URL } from '../../../../contants';
import {
  CompanyType,
  GetCompanyDocument,
  GetCompanyQuery,
  useEditCompanyMutation,
  useGetCompanyQuery,
  useOpenregCompaniesLazyQuery,
} from '../../../../gql/generated/graphql';
import { getFirstNode } from '../../../../gql/helpers';
import { usePipedriveOrganization } from '../../../../hooks/usePipedriveOrganization';
import CustomLink from '../../../DFMPage/components/CustomLink/CustomLink';
import CustomTextField from '../CustomTextField/CustomTextField';

import { validateAddressStyles } from './styles';

type Props = {
  companyId: number | null;
  onNextStep: () => void;
};

const ValidateAddressStep = ({ companyId, onNextStep }: Props) => {
  const formValuesRef = useRef(null);

  const { data } = useGetCompanyQuery({
    variables: { companyId },
  });

  const [getOpenregCompanies, { data: openregCompaniesData, loading: openregCompaniesLoading }] =
    useOpenregCompaniesLazyQuery();

  const { getPipedriveOrganization, pipedriveData, pipedriveLoading } = usePipedriveOrganization();

  const companyAddress: CompanyType = getFirstNode(data?.getCompany);
  const openregCompaniesNodes = openregCompaniesData?.getCompany?.edges?.[0]?.node?.openregCompanies?.edges?.map(
    node => node?.node
  );

  const [editCompany, { loading: isEditCompanyLoading }] = useEditCompanyMutation();
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState({
    legalName: '',
    shortName: '',
    street: '',
    city: '',
    zipCode: '',
    country: '',
    linkedinUrl: '',
    hrbNumber: '',
    court: '',
    benchmarkingIndex: '',
    sicCode: '',
    foundingYear: '',
    pipedriveId: '',
  });

  const pipedriveDeals = pipedriveData?.deals || [];
  const firstDeal = pipedriveDeals.length > 0 ? pipedriveDeals[0] : null;

  const [pipedriveEdit, setPipedriveEdit] = useState({
    organizationId: '',
    dealName: '',
    dealId: '',
    dealStage: '',
    dealStatus: '',
    lostReason: '',
  });

  useEffect(() => {
    if (companyAddress) {
      const values = {
        legalName: companyAddress?.legalName || '',
        shortName: companyAddress?.shortName || '',
        street: companyAddress?.street || '',
        city: companyAddress?.city || '',
        zipCode: companyAddress?.zipCode || '',
        country: companyAddress?.country || '',
        linkedinUrl: companyAddress?.linkedinUrl || '',
        hrbNumber: companyAddress?.hrbNumber || '',
        court: companyAddress?.court || '',
        benchmarkingIndex: companyAddress?.benchmarkingIndex?.toString() || '',
        sicCode: companyAddress?.sicCode || '',
        foundingYear: companyAddress.foundingYear?.toString() || '',
        pipedriveId: companyAddress?.pipedriveId?.toString() || '',
      };
      setFormValues(values);
      formValuesRef.current = values;
    }
  }, [companyAddress]);

  useEffect(() => {
    if (!companyId) return;
    getOpenregCompanies({ variables: { companyId } });
  }, [companyId]);

  useEffect(() => {
    if (!formValues.pipedriveId) return;
    const pipedriveId = parseInt(formValues.pipedriveId);
    if (pipedriveId) {
      getPipedriveOrganization(pipedriveId);
    }
  }, [formValues.pipedriveId, getPipedriveOrganization]);

  useEffect(() => {
    if (pipedriveData) {
      setPipedriveEdit({
        organizationId: pipedriveData.organizationId?.toString() || '',
        dealName: pipedriveData.deals?.[0]?.title || '',
        dealId: pipedriveData.deals?.[0]?.dealId?.toString() || '',
        dealStage: pipedriveData.deals?.[0]?.stage || '',
        dealStatus: pipedriveData.deals?.[0]?.status || '',
        lostReason: pipedriveData.deals?.[0]?.lostReason || '',
      });

      if (pipedriveData.organizationId) {
        setFormValues(prev => ({
          ...prev,
          pipedriveId: pipedriveData.organizationId.toString(),
        }));
      }
    } else {
      setPipedriveEdit({
        organizationId: '',
        dealName: '',
        dealId: '',
        dealStage: '',
        dealStatus: '',
        lostReason: '',
      });
    }
  }, [pipedriveData]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      const result = await editCompany({
        variables: {
          companyId,
          ...formValues,
          benchmarkingIndex: parseInt(formValues?.benchmarkingIndex),
          pipedriveId: parseInt(formValues?.pipedriveId),
          foundingYear: parseInt(formValues?.foundingYear),
        },
        update: (cache, { data: { editCompany } }) => {
          const existingData = cache.readQuery<GetCompanyQuery>({
            query: GetCompanyDocument,
            variables: { companyId },
          });

          if (existingData?.getCompany?.edges) {
            const updatedEdges = existingData.getCompany.edges.map(edge => {
              if (edge.node.id === companyAddress?.id) {
                return {
                  ...edge,
                  node: {
                    ...edge.node,
                    ...editCompany,
                  },
                };
              }
              return edge;
            });

            cache.writeQuery({
              query: GetCompanyDocument,
              variables: { companyId },
              data: {
                getCompany: {
                  ...existingData.getCompany,
                  edges: updatedEdges,
                },
              },
            });
          }
        },
      });

      if (result.data.editCompany) {
        onNextStep();
      }
    } catch (error) {
      console.error('useEditCompanyMutation error:', error);
      Sentry.captureException(error);
    }
  };

  const handleCopyAddress = row => {
    setFormValues(prev => ({
      ...prev,
      legalName: row.companyName || '',
      street: row.addressStreet || '',
      city: row.addressPlace || '',
      zipCode: row.addressZipcode || '',
      hrbNumber: row.referenceCourtCode || '',
      court: row.referenceCourtName || '',
      foundingYear: row.foundedDate || '',
    }));
  };

  const handleUndo = () => {
    setFormValues(formValuesRef.current);
  };

  const isFormEmpty = ['legalName', 'shortName', 'country'].some(field => formValues[field] === '');

  const showDataGrid = openregCompaniesNodes?.length > 0 && companyAddress?.country === 'DE';

  const handleReSearchInPd = () => {
    if (companyAddress?.uniqueDomain) {
      getPipedriveOrganization(undefined, companyAddress.uniqueDomain);
    }
  };

  const handlePipedriveEditChange = e => {
    const { name, value } = e.target;
    setPipedriveEdit(prev => ({ ...prev, [name]: value }));
  };

  const openregCompaniesColumns = [
    {
      field: 'companyName',
      headerName: t('validation.validateAddress.companiesColumnsLegalName'),
      width: 150,
    },
    {
      field: 'addressStreet',
      headerName: t('validation.validateAddress.companiesColumnsStreet'),
      width: 80,
    },
    {
      field: 'addressPlace',
      headerName: t('validation.validateAddress.companiesColumnsCity'),
      width: 80,
    },
    {
      field: 'addressZipcode',
      headerName: t('validation.validateAddress.companiesColumnsZipCode'),
      width: 80,
    },
    {
      field: 'referenceCourtCode',
      headerName: t('validation.validateAddress.companiesColumnsHrbNumber'),
      width: 100,
    },
    {
      field: 'referenceCourtName',
      headerName: t('validation.validateAddress.companiesColumnsCourt'),
      width: 100,
    },
    {
      field: 'foundedDate',
      headerName: t('validation.validateAddress.companiesColumnsFoundingYear'),
      width: 100,
      valueGetter: param => {
        return param?.slice(0, 4);
      },
    },
    {
      field: 'actions',
      headerName: '',
      editable: false,
      sortable: false,
      width: 100,
      renderCell: params => (
        <IconButton size="small" onClick={() => handleCopyAddress(params.row)}>
          <Tooltip title={'Copy'}>
            <ContentCopyOutlinedIcon />
          </Tooltip>
        </IconButton>
      ),
    },
  ];

  return (
    <Box display="flex" gap={4} flexDirection={{ xs: 'column', md: 'row' }}>
      <Box flex={1}>
        <Typography mb={2} variant="h5">
          {t('validation.validateAddress.editCompany')}
        </Typography>
        <Container
          component="form"
          onSubmit={handleSubmit}
          disableGutters
          maxWidth={false}
          sx={validateAddressStyles.container}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CustomTextField
                name="legalName"
                label={t('validation.validateAddress.legalName')}
                value={formValues.legalName}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                name="shortName"
                label={t('validation.validateAddress.shortName')}
                value={formValues.shortName}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                name="street"
                label={t('validation.validateAddress.street')}
                value={formValues.street}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                name="city"
                label={t('validation.validateAddress.city')}
                value={formValues.city}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                name="zipCode"
                label={t('validation.validateAddress.zipCode')}
                value={formValues.zipCode}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                name="country"
                label={t('validation.validateAddress.country')}
                value={formValues.country}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                name="linkedinUrl"
                label={t('validation.validateAddress.linkedinUrl')}
                value={formValues.linkedinUrl}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                name="pipedriveId"
                label={t('validation.validateAddress.pipedriveId')}
                value={formValues.pipedriveId}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                name="hrbNumber"
                label={t('validation.validateAddress.hrbNumber')}
                value={formValues.hrbNumber}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                name="court"
                label={t('validation.validateAddress.court')}
                value={formValues.court}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                name="benchmarkingIndex"
                label={t('validation.validateAddress.benchmarkingIndex')}
                value={formValues.benchmarkingIndex}
                onChange={handleChange}
                type="number"
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                name="sicCode"
                label={t('validation.validateAddress.sicCode')}
                value={formValues.sicCode}
                onChange={handleChange}
                type="number"
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                name="foundingYear"
                label={t('validation.validateAddress.foundingYear')}
                value={formValues?.foundingYear}
                onChange={handleChange}
                type="number"
              />
            </Grid>
            <Grid item xs={12} display="flex" gap={4}>
              <CustomLink sx={validateAddressStyles.link} to={PD_URL + formValues?.pipedriveId}>
                {t('validation.validateAddress.pdLink')}
              </CustomLink>
              <Button type="submit" variant="contained" color="primary" disabled={isEditCompanyLoading || isFormEmpty}>
                {t('validation.validateAddress.saveButton')}
              </Button>
              {showDataGrid && (
                <Button variant="outlined" onClick={handleUndo}>
                  {t('validation.validateAddress.undoButton')}
                </Button>
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>
      {showDataGrid && (
        <DataGrid
          paginationMode="client"
          loading={openregCompaniesLoading}
          disableRowSelectionOnClick
          rows={openregCompaniesNodes || []}
          getRowId={row => row.dbId}
          columns={openregCompaniesColumns}
          rowHeight={35}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 20,
              },
            },
          }}
          pageSizeOptions={[10]}
        />
      )}
      <Box sx={{ width: 500, mt: showDataGrid ? 2 : 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<SearchIcon />}
            onClick={handleReSearchInPd}
            disabled={!companyAddress?.uniqueDomain}
          >
            {t('validation.validateAddress.reSearchInPdButton')}
          </Button>
        </Box>
        <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2 }}>
          {pipedriveLoading && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography>{t('validation.validateAddress.loadingPipedriveData')}</Typography>
            </Box>
          )}
          <Typography fontWeight="bold" color="error.main" mb={2}>
            {t('validation.validateAddress.pipedriveInformation')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" fontWeight="bold">
                {t('validation.validateAddress.organisationId')}
              </Typography>
              <Box sx={{ border: '1px solid #ddd', p: 1, borderRadius: 1, mt: 0.5 }}>
                <TextField
                  name="organizationId"
                  value={pipedriveEdit.organizationId}
                  onChange={handlePipedriveEditChange}
                  size="small"
                  fullWidth
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" fontWeight="bold">
                {t('validation.validateAddress.dealName')}
              </Typography>
              <Box sx={{ border: '1px solid #ddd', p: 1, borderRadius: 1, mt: 0.5 }}>
                <TextField
                  name="dealName"
                  value={pipedriveEdit.dealName}
                  onChange={handlePipedriveEditChange}
                  size="small"
                  fullWidth
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" fontWeight="bold">
                {t('validation.validateAddress.dealId')}
              </Typography>
              <Box sx={{ border: '1px solid #ddd', p: 1, borderRadius: 1, mt: 0.5 }}>
                <TextField
                  name="dealId"
                  value={pipedriveEdit.dealId}
                  onChange={handlePipedriveEditChange}
                  size="small"
                  fullWidth
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" fontWeight="bold">
                {t('validation.validateAddress.dealStage')}
              </Typography>
              <Box sx={{ border: '1px solid #ddd', p: 1, borderRadius: 1, mt: 0.5 }}>
                <TextField
                  name="dealStage"
                  value={pipedriveEdit.dealStage}
                  onChange={handlePipedriveEditChange}
                  size="small"
                  fullWidth
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" fontWeight="bold">
                {t('validation.validateAddress.lostReason')}
              </Typography>
              <Box sx={{ border: '1px solid #ddd', p: 1, borderRadius: 1, mt: 0.5 }}>
                <TextField
                  name="lostReason"
                  value={pipedriveEdit.lostReason}
                  onChange={handlePipedriveEditChange}
                  size="small"
                  fullWidth
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                />
              </Box>
            </Grid>
          </Grid>
          {!pipedriveLoading &&
            (!pipedriveData ||
              (pipedriveData &&
                !pipedriveData.organizationId &&
                (!pipedriveData.deals || pipedriveData.deals.length === 0))) && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary" fontStyle="italic">
                  {t('validation.validateAddress.noMatchFound')}
                </Typography>
              </Box>
            )}
        </Box>
      </Box>
      <CustomBackdrop open={isEditCompanyLoading} />
    </Box>
  );
};

export default ValidateAddressStep;
